/**
 * DocumentEditModal Component
 * Modal for editing document metadata (manual overrides).
 * After saving, optionally triggers document indexing.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";

import type { CollectionDocument, UpdateDocumentMetadataRequest } from "../../types/admin";

interface DocumentEditModalProps {
  document: CollectionDocument;
  isOpen: boolean;
  onClose: () => void;
}

interface ReindexResult {
  status: "indexed" | "skipped";
  skipReason?: "conversion-failed" | "unavailable";
  error?: string;
}

async function updateDocumentMetadata(
  documentId: number,
  data: UpdateDocumentMetadataRequest,
): Promise<CollectionDocument> {
  const response = await fetch(`/api/document/${documentId}/metadata`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? "Failed to update document");
  }

  return response.json();
}

async function indexDocument(
  documentId: string,
): Promise<{ success: boolean; result: ReindexResult }> {
  const response = await fetch(`/api/document/${documentId}/index`, {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error ?? "Failed to index document");
  }

  const result: {
    status: "indexed" | "skipped";
    skipReason?: "conversion-failed" | "unavailable";
  } = await response.json();
  return { success: true, result };
}

type ModalPhase = "editing" | "saving" | "reindexing" | "done" | "error";

export function DocumentEditModal({ document, isOpen, onClose }: DocumentEditModalProps) {
  const queryClient = useQueryClient();

  // Form state
  const [title, setTitle] = useState(document.title ?? "");
  const [category, setCategory] = useState(document.category ?? "");
  const [documentDate, setDocumentDate] = useState(document.documentDate ?? "");
  const [governanceBody, setGovernanceBody] = useState(document.governanceBody ?? "");
  const [issuingAuthority, setIssuingAuthority] = useState(document.issuingAuthority ?? "");
  const [authorityScope, setAuthorityScope] = useState(document.authorityScope ?? "");
  const [metadataLocked, setMetadataLocked] = useState(document.metadataLocked);
  const [metadataJson, setMetadataJson] = useState(
    document.metadata ? JSON.stringify(document.metadata, null, 2) : "",
  );

  // Reindex state
  const [phase, setPhase] = useState<ModalPhase>("editing");
  const [reindexResult, setReindexResult] = useState<ReindexResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset form when document changes
  useEffect(() => {
    setTitle(document.title ?? "");
    setCategory(document.category ?? "");
    setDocumentDate(document.documentDate ?? "");
    setGovernanceBody(document.governanceBody ?? "");
    setIssuingAuthority(document.issuingAuthority ?? "");
    setAuthorityScope(document.authorityScope ?? "");
    setMetadataLocked(document.metadataLocked);
    setMetadataJson(document.metadata ? JSON.stringify(document.metadata, null, 2) : "");
    setPhase("editing");
    setReindexResult(null);
    setErrorMessage(null);
  }, [document]);

  const saveMutation = useMutation({
    mutationFn: (data: UpdateDocumentMetadataRequest) =>
      updateDocumentMetadata(document.documentId, data),
  });

  const reindexMutation = useMutation({
    mutationFn: () => indexDocument(String(document.documentId)),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updates: UpdateDocumentMetadataRequest = {};
    let parsedMetadata: Record<string, unknown> | null | undefined;

    if (metadataJson.trim().length > 0) {
      try {
        parsedMetadata = JSON.parse(metadataJson) as Record<string, unknown>;
      } catch {
        setPhase("error");
        setErrorMessage("Metadata JSON is ongeldig");
        return;
      }
    } else {
      parsedMetadata = null;
    }

    // Only include changed fields
    if (title !== (document.title ?? "")) {
      updates.title = title || undefined;
    }
    if (category !== (document.category ?? "")) {
      updates.category = (category || null) as UpdateDocumentMetadataRequest["category"];
    }
    if (documentDate !== (document.documentDate ?? "")) {
      updates.documentDate = documentDate || null;
    }
    if (governanceBody !== (document.governanceBody ?? "")) {
      updates.governanceBody = (governanceBody ||
        null) as UpdateDocumentMetadataRequest["governanceBody"];
    }
    if (issuingAuthority !== (document.issuingAuthority ?? "")) {
      updates.issuingAuthority = issuingAuthority || null;
    }
    if (authorityScope !== (document.authorityScope ?? "")) {
      updates.authorityScope = (authorityScope ||
        null) as UpdateDocumentMetadataRequest["authorityScope"];
    }
    if (metadataLocked !== document.metadataLocked) {
      updates.lock = metadataLocked;
    }

    const currentMetadata = document.metadata ?? null;
    if (JSON.stringify(parsedMetadata ?? null) !== JSON.stringify(currentMetadata)) {
      updates.metadata = parsedMetadata ?? null;
    }

    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }

    try {
      // Step 1: Save metadata
      setPhase("saving");
      setErrorMessage(null);
      await saveMutation.mutateAsync(updates);

      // Step 2: Reindex
      setPhase("reindexing");
      const { result } = await reindexMutation.mutateAsync();
      setReindexResult(result);

      if (result.status === "indexed") {
        setPhase("done");
      } else {
        setPhase("error");
        setErrorMessage(result.error ?? `Index overgeslagen: ${result.skipReason ?? "unknown"}`);
      }

      void queryClient.invalidateQueries({ queryKey: ["collectionDocuments"] });
    } catch (err) {
      setPhase("error");
      setErrorMessage(err instanceof Error ? err.message : "Onbekende fout");
    }
  };

  const handleClose = () => {
    setPhase("editing");
    setReindexResult(null);
    setErrorMessage(null);
    onClose();
  };

  if (!isOpen) return null;

  const isProcessing = phase === "saving" || phase === "reindexing";

  return (
    <div className="fixed inset-0 bg-scrim/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-container-lowest rounded-lg shadow-3 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-outline-variant">
          <h2 className="text-xl font-bold text-on-surface">Bestand bewerken</h2>
          <p className="text-sm text-on-surface-variant mt-1 truncate" title={document.name}>
            {document.name}
          </p>
        </div>

        {/* Progress / Result display */}
        {phase !== "editing" && (
          <div className="px-4 pt-4">
            <div className="space-y-2">
              <ProgressStep
                label="Metadata opslaan"
                status={(() => {
                  if (phase === "saving") return "active";
                  if (phase === "error" && !reindexResult) return "error";
                  return "done";
                })()}
              />
              <ProgressStep
                label="Herindexeren (frontmatter, Gemini, zoekindex)"
                status={(() => {
                  if (phase === "reindexing") return "active";
                  if (phase === "done") return "done";
                  if (phase === "error" && reindexResult?.status === "skipped") return "error";
                  return "pending";
                })()}
              />
            </div>

            {/* Success message */}
            {phase === "done" && reindexResult && (
              <div className="mt-3 bg-primary-container/30 border border-primary/20 text-on-surface px-4 py-3 rounded-md">
                <p className="text-sm font-semibold text-primary">✓ Opgeslagen en geherindexeerd</p>
                <p className="text-xs text-on-surface-variant mt-1">
                  De wijzigingen worden nu meegenomen in zoekresultaten.
                </p>
              </div>
            )}

            {/* Error message */}
            {phase === "error" && errorMessage && (
              <div className="mt-3 bg-error-container border border-error/30 text-on-error-container px-4 py-3 rounded-md">
                <p className="text-sm">{errorMessage}</p>
              </div>
            )}

            {/* Close button for done/error states */}
            {(phase === "done" || phase === "error") && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full px-4 py-2 bg-primary hover:bg-primary/90 text-on-primary rounded-md font-semibold transition-colors"
                >
                  Sluiten
                </button>
              </div>
            )}
          </div>
        )}

        {/* Form (only shown in editing phase) */}
        {phase === "editing" && (
          <form onSubmit={(e) => void handleSubmit(e)} className="p-4 space-y-4">
            {/* Document Subject */}
            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Titel
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titel van het document"
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Categorie
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-surface"
              >
                <option value="">-- Niet ingesteld --</option>
                <option value="statuten">Statuten</option>
                <option value="reglement">Huishoudelijk Reglement</option>
                <option value="gedragscode">Gedragscode</option>
                <option value="contract">Contract</option>
                <option value="beleidsplan">Beleidsplan</option>
                <option value="besluit">Besluit</option>
                <option value="notulen">Notulen</option>
                <option value="notitie">Notitie</option>
                <option value="jaarverslag">Jaarverslag</option>
                <option value="financieel-verslag">Financieel verslag</option>
                <option value="voorbeeld">Voorbeeld</option>
                <option value="template">Template</option>
                <option value="onbekend">Onbekend</option>
              </select>
            </div>

            {/* Document date */}
            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Datum (YYYY-MM-DD)
              </label>
              <input
                type="text"
                value={documentDate}
                onChange={(e) => setDocumentDate(e.target.value)}
                placeholder="bijv. 2026-02-20"
                pattern="\d{4}-\d{2}-\d{2}"
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Governance body */}
            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Vastgesteld door
              </label>
              <select
                value={governanceBody}
                onChange={(e) => setGovernanceBody(e.target.value)}
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-surface"
              >
                <option value="">-- Niet ingesteld --</option>
                <option value="groepsraad">Groepsraad</option>
                <option value="werkoverleg">Werkoverleg</option>
                <option value="bestuur">Bestuur</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Uitgevende autoriteit
              </label>
              <input
                type="text"
                value={issuingAuthority}
                onChange={(e) => setIssuingAuthority(e.target.value)}
                placeholder="bijv. Scoutinggroep de Bevers"
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Autoriteitsscope
              </label>
              <select
                value={authorityScope}
                onChange={(e) => setAuthorityScope(e.target.value)}
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent bg-surface"
              >
                <option value="">-- Niet ingesteld --</option>
                <option value="internal">Intern</option>
                <option value="scouting-national">Scouting nationaal</option>
                <option value="municipality">Gemeente</option>
                <option value="province">Provincie</option>
                <option value="national-government">Rijksoverheid</option>
                <option value="tax-authority">Belastingdienst</option>
                <option value="ministry">Ministerie</option>
                <option value="other">Anders</option>
                <option value="unknown">Onbekend</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-on-surface-variant mb-1">
                Metadata JSON
              </label>
              <textarea
                value={metadataJson}
                onChange={(e) => setMetadataJson(e.target.value)}
                placeholder='{"key":"value"}'
                className="w-full px-3 py-2 border border-outline-variant rounded-md focus:ring-2 focus:ring-primary focus:border-transparent min-h-28"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-on-surface">
              <input
                type="checkbox"
                checked={metadataLocked}
                onChange={(e) => setMetadataLocked(e.target.checked)}
              />
              Metadata vergrendelen
            </label>

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={isProcessing}
                className="flex-1 bg-primary hover:bg-primary/90 disabled:bg-on-surface/12 text-on-primary px-4 py-2 rounded-md font-semibold transition-colors"
              >
                Opslaan & indexeren
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-surface-container hover:bg-surface-container-high text-on-surface rounded-md font-semibold transition-colors"
              >
                Annuleer
              </button>
            </div>

            <p className="text-xs text-on-surface-variant">
              Wijzigingen worden direct doorgevoerd in de zoekindex.
            </p>
          </form>
        )}

        {/* Padding at bottom for progress view */}
        {phase !== "editing" && <div className="pb-4" />}
      </div>
    </div>
  );
}

/**
 * Progress step indicator for save + reindex flow
 */
function ProgressStep({
  label,
  status,
}: {
  label: string;
  status: "pending" | "active" | "done" | "error";
}) {
  const getIcon = (status: string) => {
    if (status === "done") return "✓";
    if (status === "active") return "⏳";
    if (status === "error") return "✗";
    return "○";
  };

  const getTextColor = (status: string) => {
    if (status === "done") return "text-primary";
    if (status === "active") return "text-on-surface";
    if (status === "error") return "text-error";
    return "text-on-surface-variant";
  };

  const icon = getIcon(status);
  const textColor = getTextColor(status);

  return (
    <div className={`flex items-center gap-2 text-sm ${textColor}`}>
      <span className="w-4 text-center">{icon}</span>
      <span className={status === "active" ? "font-semibold" : ""}>{label}</span>
      {status === "active" && (
        <span className="animate-pulse text-xs text-on-surface-variant">bezig...</span>
      )}
    </div>
  );
}
